import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { PublicClientApplication } from '@azure/msal-browser'
import * as Sentry from '@sentry/react'
import { msalConfig } from '@/lib/auth-config'
import { ThemeProvider } from '@/components/theme-provider'
import './index.css'
import App from './App'

// Sentry must be initialised before the React tree
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
  ],
  tracesSampleRate: 0.2,
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0.05,
  beforeSend(event) {
    // Never leak Authorization headers in error reports
    if (event.request?.headers) {
      delete (event.request.headers as Record<string, string>)['Authorization']
    }
    return event
  },
})

async function bootstrap() {
  // MSW mocks the API in dev by default. Set VITE_ENABLE_MSW=false to run the
  // browser against a real backend (e.g. a local `wrangler dev` on :8787).
  if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW !== 'false') {
    const { worker } = await import('./mocks/browser')
    await worker.start({ onUnhandledRequest: 'bypass' })
  }

  const msalInstance = await PublicClientApplication.createPublicClientApplication(msalConfig)

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Sentry.ErrorBoundary
        fallback={({ error }) => (
          <div className="flex min-h-screen items-center justify-center p-8 text-center">
            <div className="max-w-md">
              <h2 className="mb-2 text-xl font-semibold">Application error</h2>
              <p className="text-sm text-muted-foreground">
                {import.meta.env.DEV ? String(error) : 'An unexpected error occurred. Please refresh the page.'}
              </p>
            </div>
          </div>
        )}
      >
        <ThemeProvider>
          <App msalInstance={msalInstance} />
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>,
  )
}

bootstrap()

// Register the PWA service worker in production only. Dev runs MSW's own worker,
// and two fetch-intercepting workers would fight; the offline shell + push worker
// is a prod concern anyway. Best-effort — a failed registration never blocks the app.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* offline shell + push unavailable; the app still works online */
    })
  })
}
